import React from 'react';
import { useHasPermissionsV3, useHasAllPermissionsV3 } from '../../Utilities/CommonHooks';

type SecureComponentV3Props = {
  permissions: string[];
  requireAll?: boolean;
  fallback?: React.ReactNode;
  children?: React.ReactNode;
};

/**
 * Conditionally renders children based on v3 permission codes.
 *
 * @param permissions - Array of v3 permission codes to check (use PermissionCodesV3 constants)
 * @param requireAll  - If true, ALL permissions must be satisfied (AND). Default: false (OR).
 * @param fallback    - Optional content to render when access is denied. Default: null.
 *
 * @example
 * // Renders if user has REVIEWER.PANEL.VIEW or any parent prefix
 * <SecureComponentV3 permissions={[PermissionCodesV3.Reviewer.Panel.View]}>
 *   <PanelComponent />
 * </SecureComponentV3>
 *
 * @example
 * // Renders only if user has BOTH permissions
 * <SecureComponentV3
 *   permissions={[PermissionCodesV3.Reviewer.Profile.View, PermissionCodesV3.Reviewer.Profile.Edit]}
 *   requireAll
 * >
 *   <EditProfileButton />
 * </SecureComponentV3>
 */
export const SecureComponentV3: React.FC<SecureComponentV3Props> = ({
  permissions,
  requireAll = false,
  fallback = null,
  children,
}) => {
  const hasAny = useHasPermissionsV3();
  const hasAll = useHasAllPermissionsV3();
  const hasAccess = requireAll ? hasAll(...permissions) : hasAny(...permissions);
  return <>{hasAccess ? children : fallback}</>;
};
